import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
  Area,
} from "recharts";
import { useIsMobile } from "../../hooks/use-mobile";
import { format } from "date-fns";

interface RoiMinimalChartProps {
  data: { date: string; roi: number; isLatest?: boolean }[];
  drawdownData?: { date: string; drawdown: number }[];
}

const MinimalTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2 shadow-xl text-xs text-white font-semibold space-y-1">
        <div className="font-mono text-xs text-gray-400">{label}</div>
        {payload.map((p, idx) => (
          <div key={idx}>
            {p.dataKey === "roi" && (
              <div className="font-semibold text-emerald-300">
                {p.value}% ROI
              </div>
            )}
            {p.dataKey === "drawdown" && (
              <div className="font-semibold text-orange-400">
                {p.value}% Max Drawdown
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const RoiMinimalChart: React.FC<RoiMinimalChartProps> = ({
  data,
  drawdownData,
}) => {
  const isMobile = useIsMobile();

  if (!data?.length) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
        Not enough data yet.
      </div>
    );
  }
  const roiVals = data.map((point) => point.roi);
  const drawdownVals = drawdownData?.map((p) => p.drawdown) || [];
  const yMin =
    Math.floor(
      Math.min(...roiVals, ...(drawdownVals.length ? drawdownVals : [0]), 0)
    ) - 2;
  const yMax = Math.ceil(Math.max(...roiVals, 10)) + 2;

  let mergedData = data.map((point) => {
    const ddObj = drawdownData?.find((d) => d.date === point.date);
    return {
      ...point,
      drawdown: ddObj ? ddObj.drawdown : null,
    };
  });

  const formatXAxisDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM d");
    } catch (e) {
      return dateString;
    }
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={mergedData}
        margin={
          isMobile
            ? { top: 5, right: 5, left: -15, bottom: 10 }
            : { top: 10, right: 18, left: 0, bottom: 12 }
        }
      >
        <CartesianGrid
          stroke="#323347"
          strokeDasharray="4 5"
          vertical={false}
          opacity={0.13}
        />
        <XAxis
          dataKey="date"
          tick={{
            fill: "#cbd5e1",
            fontSize: isMobile ? 9 : 11,
            fontFamily: "inherit",
          }}
          axisLine={false}
          tickLine={false}
          minTickGap={isMobile ? 20 : 10}
          height={32}
          tickFormatter={formatXAxisDate}
        />
        <YAxis
          domain={[yMin, yMax]}
          tick={{
            fill: "#94a3b8",
            fontSize: isMobile ? 9 : 11,
            fontFamily: "inherit",
          }}
          axisLine={false}
          tickLine={false}
          width={isMobile ? 28 : 42}
          tickFormatter={(v) => v + "%"}
          tickCount={isMobile ? 4 : 6}
        />
        <Tooltip content={<MinimalTooltip />} />
        <Area
          type="monotone"
          dataKey="roi"
          stroke={undefined}
          fill="#34d39922"
          fillOpacity={1}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="roi"
          stroke="#34d399"
          strokeWidth={isMobile ? 3 : 4}
          dot={{
            r: isMobile ? 3 : 4,
            stroke: "#10b981",
            fill: "#19132ecc",
            strokeWidth: isMobile ? 1.5 : 2,
          }}
          activeDot={{
            r: isMobile ? 5 : 7,
            stroke: "#f59e42",
            fill: "#34d399",
            strokeWidth: isMobile ? 2 : 3,
          }}
          isAnimationActive={false}
          name="ROI"
        />
        <Line
          type="monotone"
          dataKey="drawdown"
          stroke="#fb923c"
          strokeWidth={isMobile ? 2 : 3}
          dot={false}
          isAnimationActive={false}
          name="Drawdown"
          strokeDasharray={isMobile ? "4 3" : "6 3"}
          connectNulls={true}
        />
        {data.map((d, i) =>
          d.isLatest ? (
            <ReferenceDot
              key={i}
              x={d.date}
              y={d.roi}
              r={isMobile ? 5.5 : 7.5}
              fill="#ecfeff"
              stroke="#34d399"
              strokeWidth={isMobile ? 3 : 4}
              isFront={true}
            />
          ) : null
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default RoiMinimalChart;
